import {
  BooleanValue,
  DateRange,
  DatetimeValue,
  DateValue,
  FieldDataType,
  FieldResponse,
  FieldValueResponse,
  FieldValueVersion,
  FieldValueWithVersionsResponse,
  isNonEmptyArray,
  localEquivalentOfUTC,
  NullableString,
  NumericFieldResponse,
  NumericValue,
  ResourceValue,
  SaveFieldValueRequest,
  SingleSelectValue,
  TextValue,
  TimeValue,
  UTCEquivalentOfLocal,
} from 'mapistry-shared';
import { getFieldValueFromForm } from '../../fieldDataType/values/EditFieldValueFactory';
import { FormValue } from '../../fieldDataType/values/EditFieldValueFactory/types';
import {
  boolFieldValueToFormInitialValue,
  dateFieldValueToFormInitialValue,
  getUnexpectedTypeErrorAndLogToSentry,
  numberFieldValueToFormInitialValue,
  resourceFieldValueToFormInitialValue,
  stringFieldValueToFormInitialValue,
} from '../../fieldDataType/values/EditFieldValueFactory/utils';
import { isWysiwygEditorEmpty } from '../../WysiwgEditor';

function isFieldValueWithVersions(
  fieldValue: FieldValueResponse | FieldValueWithVersionsResponse,
): fieldValue is FieldValueWithVersionsResponse {
  return (
    (fieldValue as FieldValueWithVersionsResponse).valueVersions !== undefined
  );
}

function composeEmptyValueVersion(): FieldValueVersion {
  return {
    effectivePeriod: {
      begin: null,
      end: null,
      bounds: '()',
    },
    notes: null,
    value: null,
  };
}

export function getFieldValueVersions(
  fieldValue?: FieldValueResponse | FieldValueWithVersionsResponse,
): FieldValueVersion[] {
  if (fieldValue && !isFieldValueWithVersions(fieldValue)) {
    throw new Error(`Field value for a versioned field doesn't have versions`);
  }
  if (fieldValue && fieldValue.valueVersions.length) {
    return fieldValue.valueVersions;
  }
  return [composeEmptyValueVersion()];
}

export type VersionFormValue = {
  value: string | Date | undefined;
  // _unit suffix is the one that numeric field uses for unit form field name
  value_unit?: NullableString;
  begin: Date | undefined;
  notes: string | undefined;
};

type FormValuesWithVersionedFields = Record<
  string,
  FormValue | VersionFormValue[]
>;

function isVersionFormValues(
  candidate: FormValue | VersionFormValue[],
): candidate is VersionFormValue[] {
  return Array.isArray(candidate);
}

export function composeEmptyVersionFormValue(): VersionFormValue {
  return {
    value: undefined,
    begin: undefined,
    notes: undefined,
  };
}

function toFormScalar(
  field: FieldResponse,
  version: FieldValueVersion,
): Pick<VersionFormValue, 'value' | 'value_unit'> {
  switch (field.type) {
    case FieldDataType.NUMERIC: {
      const { initialValue, initialUnits } = numberFieldValueToFormInitialValue(
        field as NumericFieldResponse,
        version as NumericValue,
      );
      return {
        value: initialValue,
        value_unit: initialUnits,
      };
    }
    case FieldDataType.BOOLEAN: {
      return {
        value: boolFieldValueToFormInitialValue(version as BooleanValue),
      };
    }
    case FieldDataType.DATE:
    case FieldDataType.DATETIME: {
      return {
        value: dateFieldValueToFormInitialValue(
          version as DateValue | DatetimeValue,
        ),
      };
    }
    case FieldDataType.SINGLE_SELECT:
    case FieldDataType.TEXT:
    case FieldDataType.TIME: {
      return {
        value: stringFieldValueToFormInitialValue(
          version as SingleSelectValue | TextValue | TimeValue,
        ),
      };
    }
    case FieldDataType.RESOURCE: {
      return {
        value: resourceFieldValueToFormInitialValue(version as ResourceValue),
      };
    }
    case FieldDataType.FORMULA:
    case FieldDataType.USER: {
      throw new Error(`Unsupported type for versioned field: ${field.type}`);
    }
    default: {
      const exhaustiveCheck: never = field.type;
      throw new Error(`Unimplemented field data type - ${exhaustiveCheck}`);
    }
  }
}

export function buildInitialVersionFormValue(
  field: FieldResponse,
  version: FieldValueVersion,
): VersionFormValue {
  const { begin } = version.effectivePeriod;
  return {
    ...toFormScalar(field, version),
    begin: begin ? localEquivalentOfUTC(new Date(begin)) : undefined,
    notes: version.notes ?? undefined,
  };
}

export function getVersionedFieldValueFromForm(
  field: FieldResponse,
  formValues: FormValuesWithVersionedFields,
): SaveFieldValueRequest {
  const versions = formValues[field.id];
  if (!isVersionFormValues(versions)) {
    throw getUnexpectedTypeErrorAndLogToSentry(field, versions);
  }
  const valueVersions = versions.map((version, idx, allVersions) => {
    // reuse the non-versioned extraction for the field's scalar value by
    //  passing a form-values object shaped the way a single (non-versioned) field would be
    const { fieldValue } = getFieldValueFromForm(field, {
      [field.id]: version.value,
      [`${field.id}_unit`]: version.value_unit ?? undefined,
    });
    if (!fieldValue) {
      throw getUnexpectedTypeErrorAndLogToSentry(field, versions);
    }
    const nextVersion = allVersions[idx + 1];
    const effectivePeriod: DateRange = {
      begin: version.begin ? UTCEquivalentOfLocal(version.begin) : null,
      end: nextVersion?.begin ? UTCEquivalentOfLocal(nextVersion.begin) : null,
      bounds: version.begin ? '[)' : '()',
    };
    return {
      effectivePeriod,
      fieldValue,
      notes:
        version.notes && !isWysiwygEditorEmpty(version.notes)
          ? version.notes
          : null,
    };
  });

  if (!isNonEmptyArray(valueVersions)) {
    throw getUnexpectedTypeErrorAndLogToSentry(field, versions);
  }

  return {
    fieldId: field.id,
    valueVersions,
  };
}
